import { Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { useSEOMeta, generateBreadcrumbSchema, generateWebPageSchema } from "@/components/SEO";

const coreAreas = [
  {
    id: "health",
    icon: "ri-heart-pulse-line",
    color: "from-emerald-600 to-teal-700",
    lightColor: "bg-emerald-50",
    accentColor: "text-emerald-700",
    borderColor: "border-emerald-200",
    badgeColor: "bg-emerald-100 text-emerald-700",
    title: "Health",
    tagline: "Creating a future where everybody can enjoy a life of good health and well-being.",
    summary: "BHODEV is actively driving the health agenda in Cameroon by working together with the Ministry of Public Health and other health-oriented organisations to fight against health challenges. Our staff are ready and determined in creating a healthier and better future together.",
    highlights: ["Emergency Medical Response", "Malaria & Typhoid Control", "Chronic Disease Prevention", "Community Health Education"],
    href: "/our-work/health",
    image: "https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Fdistribution-of-water-hygiene-and-sanitation-kits-to-victims-of-flood-incident-in-limbe%2FIMG-20231010-WA0001.jpg?alt=media&token=7bc91039-c9b7-49e1-bfe8-22834353dc54",
  },
  {
    id: "education",
    icon: "ri-book-open-line",
    color: "from-amber-600 to-orange-700",
    lightColor: "bg-amber-50",
    accentColor: "text-amber-700",
    borderColor: "border-amber-200",
    badgeColor: "bg-amber-100 text-amber-700",
    title: "Education",
    tagline: "Education is a fundamental human right for all children and youth.",
    summary: "BHODEV ensures that all displaced children and youths affected by the Socio-Political Crisis in Cameroon enjoy high-quality, safe and inclusive learning opportunities. We work to promote quality education for vulnerable groups, especially women and children.",
    highlights: ["First-Line Education Response", "Non-Formal Education", "Transition to Formal Education", "Youth Education & Wellbeing"],
    href: "/our-work/education",
    image: "https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Frenovation-and-reconstruction-of-bomaka-primary-school-buildings%2FIMG-20231209-WA0103.jpg?alt=media&token=1c3a574b-d92a-480d-9e14-ce603ff2c878",
  },
  {
    id: "economic-security",
    icon: "ri-seedling-line",
    color: "from-teal-600 to-cyan-700",
    lightColor: "bg-teal-50",
    accentColor: "text-teal-700",
    borderColor: "border-teal-200",
    badgeColor: "bg-teal-100 text-teal-700",
    title: "Economic Security",
    tagline: "BHODEV helps save lives and builds a better future for millions of IDPs.",
    summary: "BHODEV helps the millions of IDPs forced to flee their homes in the North-West and South-West Regions due to the socio-political crisis in Cameroon. We promote livelihoods and economic inclusion for victims, empowering women and supporting communities to become self-dependent.",
    highlights: ["Micro-Business Support", "Women Empowerment", "Sustainable Environment", "Social Protection Advocacy"],
    href: "/our-work/economic-security",
    image: "https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Fsensitization-on-water-hygiene-and-sanitation-mile-16%2FIMG-20230504-WA0066.jpg?alt=media&token=c1894e5b-f286-423f-ac50-4c54636d8865",
  },
  {
    id: "protection",
    icon: "ri-shield-check-line",
    color: "from-rose-600 to-red-700",
    lightColor: "bg-rose-50",
    accentColor: "text-rose-700",
    borderColor: "border-rose-200",
    badgeColor: "bg-rose-100 text-rose-700",
    title: "Protection",
    tagline: "Ensuring displaced people and communities are protected from violence and rights violations.",
    summary: "BHODEV stands to ensure that displaced people and communities are protected, by preventing and responding to violence, coercion, and actions taken by others to deny them of their rights. Protection is always at the heart of what we do.",
    highlights: ["Human Rights Advocacy", "GBV Prevention & Response", "Child Protection", "Psycho-Social Support"],
    href: "/our-work/protection",
    image: "https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Fsensitization-on-breast-cancer-in-buea-municiaplity%2FIMG-20231023-WA0014.jpg?alt=media&token=f4beab55-1d74-445d-a6c6-bd39ce421520",
  },
];

export default function OurWorkPage() {
  useSEOMeta({
    title: "Our Work - BHODEV Cameroon | Health, Education, Economic Security & Protection",
    description:
      "BHODEV operates across four core sectors in Cameroon: Health, Education, Economic Security, and Protection. Serving displaced and vulnerable communities in North West and South West regions since 2014.",
    keywords:
      "BHODEV our work, Cameroon NGO programs, health education Cameroon, economic security Cameroon, protection GBV Cameroon, humanitarian programs Cameroon, displaced communities Cameroon, BHODEV sectors",
    canonicalUrl: "https://bhodevcameroon.org/our-work",
    ogImage:
      "https://readdy.ai/api/search-image?query=BHODEV%20NGO%20team%20working%20in%20the%20field%20Cameroon%20diverse%20humanitarian%20workers%20community%20engagement%20health%20education%20protection%20economic%20empowerment%20wide%20landscape%20documentary%20warm%20natural%20light&width=1200&height=630&seq=ourwork-og&orientation=landscape",
    schemaData: {
      ...generateBreadcrumbSchema([
        { name: "Home", url: "https://bhodevcameroon.org/" },
        { name: "Our Work", url: "https://bhodevcameroon.org/our-work" },
      ]),
      ...generateWebPageSchema(
        "Our Work - BHODEV Cameroon",
        "BHODEV operates across four core sectors in Cameroon: Health, Education, Economic Security, and Protection.",
        "https://bhodevcameroon.org/our-work"
      ),
    },
  });

  return (
    <div className="min-h-screen bg-white font-inter">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[480px] md:h-[560px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Frehabilitation-of-water-catchment-in-wonya-mavio%2FIMG-20230510-WA0002.jpg?alt=media&token=9e123df3-93ba-421b-8f2a-3654c6e93c08"
            alt="BHODEV Our Work"
            title="BHODEV Our Work - Health, Education, Economic Security & Protection"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto w-full">
          <nav className="flex items-center justify-center gap-2 text-xs text-white/60 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white transition-colors duration-200">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white/90">Our Work</span>
          </nav>
          <span className="inline-block bg-primary/20 border border-primary/40 text-primary text-xs font-semibold px-4 py-1.5 rounded-full mb-5 tracking-wider uppercase">Core Sectors</span>
          <h1 className="text-4xl md:text-5xl font-bold mb-5 leading-tight">Our Work</h1>
          <p className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto leading-relaxed">
            BHODEV operates across four core sectors to address the most pressing humanitarian needs of displaced and vulnerable communities in Cameroon.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 px-4 md:px-8 lg:px-12 bg-stone-50">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-5">Four Core Areas of Impact</h2>
          <p className="text-stone-600 text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
            Since 2014, BHODEV has been responding to the humanitarian crisis in Cameroon&apos;s North-West and South-West regions. Our work is structured around four interconnected pillars — each addressing a critical dimension of human dignity and sustainable development.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {coreAreas.map((area) => (
              <a
                key={area.id}
                href={`#${area.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${area.badgeColor} cursor-pointer transition-all duration-200 hover:scale-105`}
              >
                <i className={`${area.icon} text-base`}></i>
                {area.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Core Areas */}
      {coreAreas.map((area, idx) => (
        <section
          key={area.id}
          id={area.id}
          className={`py-16 md:py-20 px-4 md:px-8 lg:px-12 ${idx % 2 === 0 ? "bg-white" : "bg-stone-50"}`}
          aria-labelledby={`${area.id}-title`}
        >
          <div className="max-w-6xl mx-auto">
            <div className={`flex flex-col ${idx % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"} gap-10 lg:gap-16 items-center`}>
              {/* Image */}
              <div className="w-full lg:w-1/2 shrink-0">
                <div className="relative rounded-2xl overflow-hidden">
                  <div className="w-full h-[280px] md:h-[340px]">
                    <img
                      src={area.image}
                      alt={`BHODEV ${area.title} work in Cameroon`}
                      title={`BHODEV ${area.title} work in Cameroon`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className={`absolute top-4 left-4 flex items-center gap-2 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full`}>
                    <div className={`w-5 h-5 flex items-center justify-center ${area.accentColor}`}>
                      <i className={`${area.icon} text-sm`}></i>
                    </div>
                    <span className={`text-xs font-bold ${area.accentColor}`}>{area.title}</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="w-full lg:w-1/2">
                <span className={`text-xs font-semibold uppercase tracking-widest ${area.accentColor} mb-3 block`}>Core Sector</span>
                <h2 id={`${area.id}-title`} className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">{area.title}</h2>
                <p className={`text-base font-semibold ${area.accentColor} mb-4 leading-relaxed`}>{area.tagline}</p>
                <p className="text-stone-600 text-sm md:text-base leading-relaxed mb-6">{area.summary}</p>

                {/* Highlights */}
                <div className="grid grid-cols-2 gap-2.5 mb-8">
                  {area.highlights.map((h) => (
                    <div key={h} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${area.lightColor} ${area.borderColor} border`}>
                      <div className={`w-4 h-4 flex items-center justify-center ${area.accentColor} shrink-0`}>
                        <i className="ri-check-line text-xs font-bold"></i>
                      </div>
                      <span className="text-xs font-medium text-stone-700">{h}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to={area.href}
                  className={`inline-flex items-center gap-2 bg-gradient-to-r ${area.color} text-white px-6 py-3 rounded-full text-sm font-semibold hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer`}
                >
                  Explore {area.title}
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="py-16 px-4 md:px-8 lg:px-12 bg-stone-800 text-white text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Support Our Work</h2>
          <p className="text-stone-300 text-base mb-8 leading-relaxed">
            Your support enables BHODEV to continue delivering life-changing programmes across all four core sectors in Cameroon.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-involved" className="bg-primary text-white px-8 py-3.5 rounded-full font-semibold hover:bg-primary-dark transition-colors duration-300 whitespace-nowrap cursor-pointer">
              Get Involved
            </Link>
            <Link to="/contact" className="border border-white/30 text-white px-8 py-3.5 rounded-full font-semibold hover:bg-white/10 transition-colors duration-300 whitespace-nowrap cursor-pointer">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
